import React, { useState, useEffect, useRef } from 'react';
import { Hits, useSearchBox } from 'react-instantsearch';
import { useDebounceValue, useOnClickOutside } from 'usehooks-ts';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchHit from './SearchHit';
import { translate } from '../../services/Translations';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';

const SearchInput = () => {
  const navigate = useNavigate();
  const { refine, query } = useSearchBox();
  const [hitsVisible, setHitsVisible] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useDebounceValue('', 300);

  const hitsRef = useRef(null);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const searchQueryValue = queryParams.get('query');
  const [translatedPlaceholder, setTranslatedPlaceholder] = useState('');

  useEffect(() => {
    setDebouncedQuery(currentQuery);
  }, [currentQuery]);

  useEffect(() => {
    setCurrentQuery(searchQueryValue || '');
  }, [searchQueryValue]);

  useEffect(() => {
    if (debouncedQuery) {
      refine(debouncedQuery);
    }
  }, [debouncedQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuery(e.target.value);
    setHitsVisible(!!e.target.value);
  };

  const handleSearchFocus = () => {
    if (currentQuery && !hitsVisible) {
      setHitsVisible(true);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    if (currentQuery) {
      navigate({
        pathname: '/tooted',
        search: `?query=${currentQuery}`,
      });
      handleCloseHits();
    }

    event.preventDefault();
  };

  const handleCloseHits = () => {
    setHitsVisible(false);
  };

  const handleClickOutsideHits = () => {
    handleCloseHits();
  };

  useOnClickOutside(hitsRef, handleClickOutsideHits);

  useEffect(() => {
    const translatePlaceholder = async () => {
      const translated = await translate('search', 'search');
      setTranslatedPlaceholder(translated);
    };

    translatePlaceholder();
  }, []);

  return (
    <div>
      <form onSubmit={handleSearchSubmit} role="search">
        <div className="relative w-full lg:w-2/5 h-12 lg:h-10">
          <input
            id="search-input"
            className="w-full h-full py-2.5 pl-3 pr-12 text-base lg:text-sm border-0 rounded-sm bg-rgrey-light focus:ring-0"
            value={currentQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            placeholder={translatedPlaceholder}
            aria-label={translatedPlaceholder}
          />
          <button
            type="submit"
            onClick={handleSearchSubmit}
            className="absolute text-rgrey-dark -translate-y-1/2 top-1/2 right-3 z-40"
            aria-label={translatedPlaceholder}
          >
            <MagnifyingGlassIcon
              className="w-6 h-6 text-black"
              aria-hidden="true"
            />
          </button>
        </div>
      </form>

      {hitsVisible && query && (
        <div
          ref={hitsRef}
          className="absolute left-0 w-full bg-white drop-shadow-sm mt-1 lg:mt-2 p-4 flex max-h-[66vh]"
        >
          <Hits
            hitComponent={(props) => (
              <SearchHit {...props} close={handleCloseHits} />
            )}
            classNames={{
              root: 'max-h-full overflow-y-auto',
              list: 'flex flex-wrap ',
              item: 'w-1/2 sm:w-1/3 md:w-1/4 xl:w-1/5 2xl:w-1/6',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SearchInput;
